import React, { useState, useRef, useEffect } from "react";
import { useStyles } from "./TakisNav.styles";
import { Link } from "@material-ui/core";
import { useLanguage, supportedLanguages } from "../hooks/useLanguage";
import PropTypes from "prop-types";
import { CaretSvg } from "./TakisSvgs";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";

const TakisDropdown = (props) => {
  const hasLink = props.link != '/';
  // so it matches in another language,
  const classes = useStyles();
  const theme = useTheme();

  const language = useLanguage();
  const isSpanish = language === supportedLanguages.es;
  const openNewTab = isSpanish ? "(abre en una nueva ventana)" : "(opens in new window)";

  const toggleTitlePre = language === supportedLanguages.en
      ? 'expand'
      : 'mostrar';
  const toggleTitlePreOpened = language === supportedLanguages.en
      ? 'collapse'
      : 'ocultar';

  const onMobile = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.lg)
  );

  const onXl = useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.xl));

  const fixedArea = useRef();
  const dropdownRoot = useRef();
  const [left, setLeft] = useState();
  const [isDropOpen, setIsDropOpen] = useState(false);

  const selectedMenu = useRef(null);
  const selectedMenuToggle = useRef(null);
  const dropdownItems = useRef(null);
  const dropdownListItems = useRef([]);

  const resizeObserver = React.useRef(
    new ResizeObserver((entries) => {
      // Only care about the first element, we expect one element ot be watched
      if (dropdownRoot.current) {
        const position = dropdownRoot.current.getBoundingClientRect();
        setLeft(position.left);
      }
      if (fixedArea.current) {
        const areaRect = fixedArea.current.getBoundingClientRect();
        const menuHeight =
          window.innerWidth >= theme.breakpoints.values.xl
            ? areaRect.height
            : 0;
        props.setMenuHeight(menuHeight);
      }
    })
  );

  // At dropdown transition end only on image drawer
  const animationEnd = (event) => {
    if (event.propertyName === 'max-height') {
        if (isDropOpen) {
          focusFirstOnList()
        } else {
          focusDropdownSelectedMenu()
        }
    }
  };

  useEffect(() => {
    const resize = resizeObserver.current;
    if (fixedArea.current) {
      resize.observe(fixedArea.current);
    }
    const currentRoot = dropdownRoot.current;
    const currentFixedArea = fixedArea.current;

    return () => {
      if (currentRoot) {
        const position = currentRoot.getBoundingClientRect();
        setLeft(position.left);
      }
      if (currentFixedArea) {
        resize.unobserve(currentFixedArea);
      }
    };
  }, [fixedArea, resizeObserver, dropdownRoot]);

  const dropdownToggle = () => {
    setIsDropOpen(!isDropOpen);
  };

  const focusFirstOnList = () => {
    dropdownListItems.current[0].focus();
  }

  const focusDropdownSelectedMenu = () => {
    selectedMenuToggle.current.focus();

  }

  // Will make menu show images horizontally
  const hasImages = props.showImages;

  const handleKeyDown = (event) => {
    // handle enter
    if (event.keyCode === 27 && isDropOpen) {
      setIsDropOpen(false);
    }
    else if ((event.keyCode === 39 || event.keyCode === 40) && isDropOpen) {
      let containerSibling = event.target.parentNode.nextSibling;
      if (containerSibling) {
        let nextElement = containerSibling.firstChild;
        nextElement.focus();
      }
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    else if ((event.keyCode === 37 || event.keyCode === 38)  && isDropOpen) {
      let containerSibling = event.target.parentNode.previousSibling;
      if (containerSibling) {
        let previousElement = containerSibling.firstChild;
        previousElement.focus();
      }
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  useEffect(() => {

    const handleFocusIn = (event) => {

      const dropdown = dropdownItems.current
      const dropdownRootItems = dropdownRoot.current

      const dropdownContains = dropdown?.contains(event.target)
      const dropdownRootItemsContains = dropdownRootItems?.contains(event.target)

      const isContained =dropdownContains || dropdownRootItemsContains

      if (
        !dropdown &&
        !dropdownRootItems &&
        !isContained
      ) {
        setIsDropOpen(false)
      }
    };

    document.addEventListener("focusin", handleFocusIn, true);
    // every time you add it, you also remove it when props.location.pathname changes
    return () => {
      document.removeEventListener("focusin", handleFocusIn, true);
    }
  }, [
    dropdownItems,
    dropdownRoot
  ]);

  return (
    <div
      className={`${classes.nav_options} ${classes.dropdown_manager}
      ${isDropOpen ? classes.drop_link_active : ""}`}
      onMouseEnter={onMobile ? () => {} : () => setIsDropOpen(true)}
      onMouseLeave={onMobile ? () => {} : () => setIsDropOpen(false)}
      ref={dropdownRoot}
    >
      {/* Product button Landing page link */}
      {
        <>
          {hasLink ? (
            <Link
            color="initial"
            // Sets Style overrides proper whe it's link or button Open or closed
            className={`
              ${classes.menu_link}
              ${isDropOpen ? "isOpen" : "isClosed"}
            `}
            // Adds link when linkable (when in desktop and open and product or when it's mobile)
            href={props.link}
            ref={selectedMenu}
          >
            {props.title}
          </Link>
          ) : (
            <Link
            color="initial"
            // Sets Style overrides proper whe it's link or button Open or closed
            className={`
              ${classes.menu_link}
              ${isDropOpen ? "isOpen" : "isClosed"}
              ${classes.noUnderline}
            `}
            // Adds link when linkable (when in desktop and open and product or when it's mobile)
            ref={selectedMenu}
          >
            {props.title}
          </Link>
          )}
          <button
            className={classes.caretButton}
            onClick={dropdownToggle}
            ref={selectedMenuToggle}
            aria-controls={props.linkType + "-id"}
            aria-expanded={isDropOpen}
            title={
              language === supportedLanguages.en
                ? `${isDropOpen ? toggleTitlePreOpened : toggleTitlePre} ${props.title}`
                // Reverted in spanish
                : `${isDropOpen ? toggleTitlePreOpened : toggleTitlePre} ${props.title}`
            }
            >
            <CaretSvg
              state={isDropOpen}
              svg_class={classes.caret_svg}
              ariaHidden="true"
              />
          </button>
        </>
      }
      <div
        className={`
          ${classes.dropDown_fixed}
          ${hasImages ? "isProduct" : ""}
          ${isDropOpen ? "isOpen" : "isClosed"}
          ${onMobile && props.linkType === 'products' ? "isDisabled" : ""}
        `}
        id={props.linkType + "-id"}
        onTransitionEnd={hasImages && onXl ? animationEnd : () => {}}
        ref={fixedArea}
        style={hasImages && onXl ? { left: `-${left}px` } : { left: "unset" }}
      >
        <ul
          className={`
            ${classes.dropDown_content}
            ${hasImages ? "isProduct" : ""}
            ${isDropOpen ? "isOpen" : "isClosed"}
            ${isDropOpen ? classes.dropDown_content_open : ""}
          `}
          ref={dropdownItems}
        >
          {props.options.map((item, index) => {
            return item.type === "other" ? (
              <li
                key={item.id}
                className={classes.listItemDropOptions}
                >
                <a
                  className={classes.drop_options}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${item.title} ${openNewTab}`}
                  ref={(el) => (dropdownListItems.current[index] = el)}
                  onClick={props.clickFunc}
                  onKeyDown={handleKeyDown}
                >
                  {item.title}
                </a>
              </li>
            ) : hasImages ? (
              <li
                key={item.id}
                className={classes.listItemDropOptions}
                >
                <Link
                  color="initial"
                  className={classes.drop_options}
                  href={item.link}
                  onClick={props.clickFunc}
                  ref={(el) => (dropdownListItems.current[index] = el)}
                  onKeyDown={handleKeyDown}
                >
                  <img
                    className={classes.drop_options_image}
                    alt={item.title}
                    src={item.image_url}
                  />
                  {item.title}
                </Link>
              </li>
            ) : (
              // not a product
              <li
                key={item.id}
                className={classes.listItemDropOptions}
                >
                <Link
                  color="initial"
                  className={classes.drop_options}
                  href={item.link}
                  ref={(el) => (dropdownListItems.current[index] = el)}
                  onClick={props.clickFunc}
                  onKeyDown={handleKeyDown}
                >
                  {item.title}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

TakisDropdown.propTypes = {
  title: PropTypes.string,
  options: PropTypes.array,
};

export default TakisDropdown;
